import React, { createContext, useContext, useMemo } from 'react';
import { useLocale } from '../hooks/useLocale';
import type { Locale } from '../types/locale';

interface LocaleContextType {
  locale: Locale;
  changeLocale: (locale: Locale) => void;
}

// Provide a default context value to prevent React error #419 during SSR/hydration
const defaultLocaleContext: LocaleContextType = {
  locale: 'en',
  changeLocale: () => {},
};

const LocaleContext = createContext<LocaleContextType>(defaultLocaleContext);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale, changeLocale } = useLocale();

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({ locale, changeLocale }), [locale, changeLocale]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocaleContext = () => {
  const context = useContext(LocaleContext);
  return context;
};